import { apiClient } from './client'
import type { ApiResponse } from '@/types/api'

// ============================================================================
// Types
// ============================================================================

export interface CurriculumSummary {
  id: number
  title: string
  bareTitle: string | null
  description: string | null
  shortDescription: string | null
  thumbnailUrl: string | null
  starRating: number | null
  courseCount: number
  totalLessonCount: number
  estimatedDurationMinutes: number | null
}

export interface CurriculumListResponse {
  curricula: CurriculumSummary[]
}

export interface CourseInCurriculum {
  id: number
  title: string
  bareTitle: string | null
  description: string | null
  shortDescription: string | null
  thumbnailUrl: string | null
  sequenceOrder: number
  overallSequenceOrder: number | null
  moduleCount: number
  lessonCount: number
  estimatedDurationMinutes: number | null
}

export interface CurriculumDetailResponse {
  id: number
  title: string
  bareTitle: string | null
  description: string | null
  shortDescription: string | null
  thumbnailUrl: string | null
  learningObjectives: string | null
  concepts: string | null
  starRating: number | null
  estimatedDurationMinutes: number | null
  courses: CourseInCurriculum[]
}

export interface LessonSummary {
  id: number
  title: string
  bareTitle: string | null
  description: string | null
  shortDescription: string | null
  thumbnailUrl: string | null
  qid: string | null
  sequenceOrder: number
  overallSequenceOrder: number | null
  estimatedDurationMinutes: number | null
}

export interface ModuleInCourse {
  id: number
  title: string
  bareTitle: string | null
  description: string | null
  shortDescription: string | null
  thumbnailUrl: string | null
  sequenceOrder: number
  overallSequenceOrder: number | null
  lessons: LessonSummary[]
  estimatedDurationMinutes: number | null
}

export interface CourseDetailResponse {
  id: number
  title: string
  bareTitle: string | null
  description: string | null
  shortDescription: string | null
  thumbnailUrl: string | null
  learningObjectives: string | null
  concepts: string | null
  starRating: number | null
  estimatedDurationMinutes: number | null
  curriculumId: number | null
  modules: ModuleInCourse[]
}

export interface LessonDetailResponse {
  id: number
  title: string
  bareTitle: string | null
  description: string | null
  shortDescription: string | null
  thumbnailUrl: string | null
  learningObjectives: string | null
  concepts: string | null
  qid: string | null
  estimatedDurationMinutes: number | null
  nextLessonId: number | null
  prevLessonId: number | null
  courseId?: number
  moduleId?: number
}

/**
 * Queture 영상 정보 응답
 * 백엔드 QuetureResponse DTO와 매핑
 */
export interface QuetureResponse {
  quetureId: string
  title: string | null
  hlsUrl: string | null
  thumbnailUrl: string | null
  duration: number | null
}

// ============================================================================
// API Functions
// ============================================================================

export const contentApi = {
  /**
   * 모든 커리큘럼 목록 조회
   */
  getCurricula: async (): Promise<CurriculumSummary[]> => {
    const response = await apiClient.get<ApiResponse<CurriculumListResponse>>('/content/curricula')
    return response.data.data?.curricula || []
  },

  /**
   * 특정 커리큘럼 상세 조회
   */
  getCurriculumDetail: async (curriculumId: number): Promise<CurriculumDetailResponse> => {
    const response = await apiClient.get<ApiResponse<CurriculumDetailResponse>>(
      `/content/curricula/${curriculumId}`
    )
    if (!response.data.data) {
      throw new Error('Curriculum not found')
    }
    return response.data.data
  },

  /**
   * Course 상세 조회
   */
  getCourseDetail: async (courseId: number): Promise<CourseDetailResponse> => {
    const response = await apiClient.get<ApiResponse<CourseDetailResponse>>(
      `/content/courses/${courseId}`
    )
    if (!response.data.data) {
      throw new Error('Course not found')
    }
    return response.data.data
  },

  /**
   * Lesson 상세 조회
   */
  getLessonDetail: async (lessonId: number): Promise<LessonDetailResponse> => {
    const response = await apiClient.get<ApiResponse<LessonDetailResponse>>(
      `/content/lessons/${lessonId}`
    )
    if (!response.data.data) {
      throw new Error('Lesson not found')
    }
    return response.data.data
  },

  /**
   * Queture 영상 정보 조회
   * QID로 Queture 영상의 HLS URL, 썸네일 등 정보를 조회합니다.
   * 백엔드가 레거시 API를 프록시하여 영상 정보를 반환합니다.
   */
  getQuetureInfo: async (contentId: number, quetureId: string): Promise<QuetureResponse> => {
    const response = await apiClient.get<ApiResponse<QuetureResponse>>(
      `/content/${contentId}/quetures/${quetureId}`
    )
    if (!response.data.data) {
      throw new Error('Queture not found')
    }
    return response.data.data
  }
}

